import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Play, Pause, Square, Clock, MapPin, Tv, ChevronLeft,
  Radio, RefreshCw, Maximize2, Minimize2, Settings,
  AlertTriangle, Trophy, Users, Timer, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  scheduled: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/40', label: 'Scheduled' },
  warmup: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/40', label: 'Warmup' },
  live: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/40', label: 'LIVE' },
  halftime: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/40', label: 'Halftime' },
  final: { bg: 'bg-zinc-500/20', text: 'text-zinc-400', border: 'border-zinc-500/40', label: 'Final' },
};

function formatClock(ms) {
  if (!ms || ms <= 0) return '0:00';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function ScoreboardTicker({ games, event }) {
  const scrollRef = useRef(null);

  const liveGames = games.filter(g => g.status === 'live');
  const activeGames = games.filter(g => ['live', 'halftime', 'warmup'].includes(g.status));
  const completedGames = games.filter(g => g.status === 'final');

  return (
    <div className="bg-gradient-to-r from-zinc-900 via-black to-zinc-900 border-b border-zinc-800" data-testid="scoreboard-ticker">
      {/* Top Status Bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800/50">
        <div className="flex items-center gap-4">
          <h2 className="text-white font-bold text-sm tracking-wider uppercase flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-400" />
            {event?.name || 'Game Day'}
          </h2>
          {event?.location && (
            <span className="text-zinc-500 text-xs flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {event.location}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs">
          {liveGames.length > 0 && (
            <span className="flex items-center gap-1.5 text-red-400">
              <span className="h-2 w-2 bg-red-500 rounded-full animate-pulse" />
              {liveGames.length} Live
            </span>
          )}
          <span className="text-zinc-500">{activeGames.length} Active</span>
          <span className="text-zinc-600">{completedGames.length} Final</span>
          <span className="text-zinc-600">{games.length} Total</span>
        </div>
      </div>

      {/* Scrolling Ticker */}
      <div className="overflow-x-auto scrollbar-hide" ref={scrollRef}>
        <div className="flex gap-1 px-2 py-2 min-w-max">
          {games.map(game => {
            const sc = STATUS_COLORS[game.status] || STATUS_COLORS.scheduled;
            return (
              <div
                key={game.game_id}
                className={`flex items-center gap-3 px-3 py-1.5 rounded border ${sc.border} ${sc.bg} min-w-[200px] transition-all`}
                data-testid={`ticker-game-${game.game_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-white text-xs font-medium truncate">{game.home_team_name}</span>
                    <span className="text-white font-bold text-sm ml-2">{game.score?.home ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-xs truncate">{game.away_team_name}</span>
                    <span className="text-zinc-300 font-bold text-sm ml-2">{game.score?.away ?? 0}</span>
                  </div>
                </div>
                <div className="text-center">
                  <Badge className={`${sc.bg} ${sc.text} text-[9px] px-1 py-0`}>{sc.label}</Badge>
                  {game.status === 'live' && game.clock && (
                    <div className="text-[10px] text-zinc-500 mt-0.5">
                      Q{game.clock?.period || 1} {formatClock(game.clock?.time_remaining_ms)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {games.length === 0 && (
            <div className="text-zinc-600 text-xs px-4 py-2">No games scheduled for this event</div>
          )}
        </div>
      </div>
    </div>
  );
}

function GameCard({ game, token, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const sc = STATUS_COLORS[game.status] || STATUS_COLORS.scheduled;

  const updateStatus = async (newStatus) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/engine/games/${game.game_id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        toast.success(`Game ${newStatus === 'live' ? 'started' : newStatus === 'halftime' ? 'paused (halftime)' : newStatus === 'final' ? 'ended' : 'updated'}`);
        onUpdate();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update game');
      }
    } catch {
      toast.error('Failed to update game status');
    }
    setLoading(false);
  };

  return (
    <Card className={`bg-zinc-900/80 border ${sc.border} hover:border-opacity-100 transition-all`} data-testid={`gameday-card-${game.game_id}`}>
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Badge className={`${sc.bg} ${sc.text} text-[10px]`}>
              {game.status === 'live' && <span className="h-1.5 w-1.5 bg-red-500 rounded-full animate-pulse mr-1 inline-block" />}
              {sc.label}
            </Badge>
            {game.court && <span className="text-zinc-600 text-[10px]">Court {game.court}</span>}
          </div>
          {game.status === 'live' && game.clock && (
            <div className="flex items-center gap-1.5 text-zinc-400 text-xs">
              <Timer className="h-3 w-3" />
              <span>Q{game.clock?.period || 1}</span>
              <span className="font-mono">{formatClock(game.clock?.time_remaining_ms)}</span>
            </div>
          )}
        </div>

        {/* Scoreboard */}
        <div className="space-y-2 mb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="h-8 w-1 rounded-full" style={{ backgroundColor: game.home_team_color || '#ef4444' }} />
              <span className="text-white font-semibold text-sm truncate">{game.home_team_name}</span>
            </div>
            <span className={`text-2xl font-bold tabular-nums ${game.score?.home > game.score?.away ? 'text-white' : 'text-zinc-500'}`}>
              {game.score?.home ?? 0}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="h-8 w-1 rounded-full" style={{ backgroundColor: game.away_team_color || '#3b82f6' }} />
              <span className="text-zinc-300 font-semibold text-sm truncate">{game.away_team_name}</span>
            </div>
            <span className={`text-2xl font-bold tabular-nums ${game.score?.away > game.score?.home ? 'text-white' : 'text-zinc-500'}`}>
              {game.score?.away ?? 0}
            </span>
          </div>
        </div>

        {/* Game info */}
        {(game.location || game.sport) && (
          <div className="flex items-center gap-3 text-[10px] text-zinc-600 mb-3">
            {game.sport && <span className="capitalize">{game.sport}</span>}
            {game.location && <span className="flex items-center gap-0.5"><MapPin className="h-2.5 w-2.5" />{game.location}</span>}
          </div>
        )}

        {/* Controls */}
        <div className="flex items-center gap-1.5 pt-2 border-t border-zinc-800">
          {game.status === 'scheduled' && (
            <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs h-8" onClick={() => updateStatus('live')} disabled={loading} data-testid={`start-game-${game.game_id}`}>
              <Play className="h-3 w-3 mr-1" /> Start
            </Button>
          )}
          {game.status === 'warmup' && (
            <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs h-8" onClick={() => updateStatus('live')} disabled={loading}>
              <Play className="h-3 w-3 mr-1" /> Go Live
            </Button>
          )}
          {game.status === 'live' && (
            <>
              <Button size="sm" className="flex-1 bg-amber-600 hover:bg-amber-700 text-white text-xs h-8" onClick={() => updateStatus('halftime')} disabled={loading} data-testid={`pause-game-${game.game_id}`}>
                <Pause className="h-3 w-3 mr-1" /> Half
              </Button>
              <Button size="sm" className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white text-xs h-8" onClick={() => updateStatus('final')} disabled={loading} data-testid={`end-game-${game.game_id}`}>
                <Square className="h-3 w-3 mr-1" /> End
              </Button>
            </>
          )}
          {game.status === 'halftime' && (
            <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs h-8" onClick={() => updateStatus('live')} disabled={loading}>
              <Play className="h-3 w-3 mr-1" /> Resume
            </Button>
          )}
          {game.status === 'final' && (
            <div className="flex-1 text-center text-zinc-600 text-xs py-1">Game Complete</div>
          )}
          <Link to={`/operator/game/${game.game_id}`}>
            <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 text-xs h-8" data-testid={`operator-link-${game.game_id}`}>
              <Settings className="h-3 w-3" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

export default function GameDayMode() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { token, canAccessAdmin } = useAuth();
  const [games, setGames] = useState([]);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const containerRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      // Fetch event details
      const eventRes = await fetch(`${API_URL}/api/events/${eventId}`, { headers });
      if (eventRes.ok) {
        const eventData = await eventRes.json();
        setEvent(eventData);
      }

      // Fetch games for this event
      const gamesRes = await fetch(`${API_URL}/api/engine/games?event_id=${eventId}&include_hidden=true&limit=100`, { headers });
      if (gamesRes.ok) {
        const gamesData = await gamesRes.json();
        // Sort: live first, then halftime/warmup, then scheduled, then final
        const statusOrder = { live: 0, halftime: 1, warmup: 2, scheduled: 3, final: 4 };
        gamesData.sort((a, b) => (statusOrder[a.status] ?? 5) - (statusOrder[b.status] ?? 5));
        setGames(gamesData);
      }
    } catch (err) {
      console.error('Failed to fetch game day data:', err);
    }
    setLoading(false);
  }, [eventId, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 5 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setFullscreen(true);
    } else {
      document.exitFullscreen();
      setFullscreen(false);
    }
  };

  useEffect(() => {
    const handler = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  if (!canAccessAdmin()) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-white text-xl font-bold mb-2">Access Restricted</h2>
          <p className="text-zinc-500 mb-4">Game Day Mode is available to admins and operators only.</p>
          <Button onClick={() => navigate('/events')} className="bg-rooam-red hover:bg-red-600">Back to Events</Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 text-rooam-red animate-spin mx-auto mb-3" />
          <p className="text-zinc-500">Loading Game Day...</p>
        </div>
      </div>
    );
  }

  const liveCount = games.filter(g => g.status === 'live').length;

  return (
    <div ref={containerRef} className="min-h-screen bg-black" data-testid="game-day-mode">
      {/* Scoreboard Ticker */}
      <ScoreboardTicker games={games} event={event} />

      {/* Controls Bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-zinc-900/50 border-b border-zinc-800/50">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white" onClick={() => navigate(-1)} data-testid="back-btn">
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <h1 className="text-white font-bold text-lg hidden sm:block">
            Command Center
          </h1>
          {liveCount > 0 && (
            <Badge className="bg-red-500/20 text-red-400 text-xs">
              <Radio className="h-3 w-3 mr-1 animate-pulse" /> {liveCount} Live
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className={`text-xs ${autoRefresh ? 'text-green-400' : 'text-zinc-600'}`}
            onClick={() => setAutoRefresh(!autoRefresh)}
            data-testid="auto-refresh-toggle"
          >
            <RefreshCw className={`h-3 w-3 mr-1 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Live' : 'Paused'}
          </Button>
          <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white" onClick={toggleFullscreen}>
            {fullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>

      {/* Game Cards Grid */}
      <div className="p-4">
        {games.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Trophy className="h-16 w-16 text-zinc-800 mb-4" />
            <h3 className="text-zinc-500 text-lg font-medium mb-2">No Games Yet</h3>
            <p className="text-zinc-600 text-sm mb-4">Add games to this event to use Game Day Mode</p>
            <Link to="/admin">
              <Button className="bg-rooam-red hover:bg-red-600">Go to Admin Dashboard</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="game-cards-grid">
            {games.map(game => (
              <GameCard key={game.game_id} game={game} token={token} onUpdate={fetchData} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
